/**
 * usePreAuthFeatureFlags Hook
 * 
 * For checking feature flags BEFORE user authentication.
 * Used by SignupPage.js, Onboarding.js, and other pre-auth gating scenarios.
 * 
 * This is separate from useFeatureFlags() which requires authentication
 * and fetches from /api/feature-flags/user.
 * 
 * Uses the public /api/feature-flags endpoint (no auth required).
 * Fail-closed: if the check fails, assumes flags are disabled for safety.
 */
import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const usePreAuthFeatureFlags = () => {
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFlags = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/feature-flags`);
      
      if (response.ok) {
        const data = await response.json();
        setFlags(data);
      } else {
        // Fail-closed: if endpoint fails, assume all flags are disabled
        console.warn('Pre-auth feature flags endpoint returned non-OK status');
        setFlags({});
        setError('Failed to fetch feature flags');
      }
    } catch (err) {
      // Fail-closed: if network error, assume all flags are disabled
      console.warn('Could not check pre-auth feature flags:', err.message);
      setFlags({});
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  /**
   * Check if a specific flag is enabled.
   * Fail-closed: returns false if flag is not explicitly true.
   */
  const isEnabled = useCallback((flagKey) => {
    return flags[flagKey] === true;
  }, [flags]);

  /**
   * Specifically check if employer platform is live.
   * This is the most common use case for pre-auth flag checking.
   * Fail-closed: returns false if not explicitly true.
   */
  const isEmployerPlatformLive = useCallback(() => {
    return flags.employer_platform_live === true;
  }, [flags]);

  return {
    flags,
    loading,
    error,
    isEnabled,
    isEmployerPlatformLive,
    refetch: fetchFlags,
  };
};

export default usePreAuthFeatureFlags;
